import React, { useEffect, useRef, useState } from "react";
import UseContext from "../../../hooks/useContext";
import {
  BuildingCompanyIcon,
  CheckIcon,
  ChevronDown,
} from "../../../assets/icons";
import ApiDatabase from "../../../server";
import ExternalTooltip from "../../tooltips/ExternalTooltip";

const TUTO_SWITCH_COMPANY_CONFIRMED_KEY = "tutoSwitchCompanyConfirmed";

const SwitchCompany = () => {
  const {
    userData,
    setUserData,
    companySelected,
    setCompanySelected,
    companyList,
    setCompanyList,
    refreshCompany,
    setRefreshCompany,
  } = UseContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [confirmTutoSwitchCompany, setConfirmTutoSwitchCompany] = useState(
    () => localStorage.getItem(TUTO_SWITCH_COMPANY_CONFIRMED_KEY) === "true",
  );
  const tutoDismissedRef = useRef(
    localStorage.getItem(TUTO_SWITCH_COMPANY_CONFIRMED_KEY) === "true",
  );
  const [cachedCompanies, setCachedCompanies] = useState(() =>
    companyList.length > 0 ? companyList : [],
  );
  const [isWaiting, setIsWaiting] = useState(() => companyList.length === 0);
  const dropDownRef = useRef(null);
  const url = window.location.href;
  const createOfMission = url.includes("createOfMission");

  useEffect(() => {
    if (companyList.length > 0) {
      setCachedCompanies(companyList);
      setIsWaiting(false);
    }
  }, [companyList]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setIsWaiting(false);
      return;
    }
    setIsWaiting(true);
    ApiDatabase.getCompaniesMissions(
      {},
      (data) => {
        const selectedCompanyIndex = companySelected
          ? data.companies?.findIndex(
              (company) => company._id === companySelected.idCompany,
            )
          : -1;

        const list =
          data.companies?.map((company, index) => ({
            idCompany: company._id,
            name: company.companyName || company.name || "Entreprise",
            syncDate: company.lastSyncDate,
            badgerName: company.badgerName,
            checked:
              index ===
              (selectedCompanyIndex !== -1 ? selectedCompanyIndex : 0),
          })) || [];

        if (list.length > 0 && selectedCompanyIndex === -1) {
          setCompanySelected(list[0]);
        }

        setCompanyList(list);
        setCachedCompanies(list);
        setIsWaiting(false);
      },
      (err) => {
        console.warn(err);
        setIsWaiting(false);
      },
    );
  }, [companySelected, setCompanyList, setCompanySelected]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropDownRef.current &&
        !dropDownRef.current.contains(e.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (Object.keys(userData).length === 0) return;
    if (tutoDismissedRef.current) return;
    if (localStorage.getItem(TUTO_SWITCH_COMPANY_CONFIRMED_KEY) === "true") {
      tutoDismissedRef.current = true;
      setConfirmTutoSwitchCompany(true);
      return;
    }
    setConfirmTutoSwitchCompany(userData.tutoSwitchCompany ?? false);
  }, [userData]);

  const dismissTutoSwitchCompany = () => {
    tutoDismissedRef.current = true;
    localStorage.setItem(TUTO_SWITCH_COMPANY_CONFIRMED_KEY, "true");
    setConfirmTutoSwitchCompany(true);

    if (!localStorage.getItem("token")) {
      console.warn(
        "Session absente : impossible d’enregistrer la confirmation du tutoriel multi-entreprise.",
      );
      return;
    }

    ApiDatabase.putCheckTutoSwitchCompany(
      {},
      () => {
        setUserData((prev) =>
          typeof prev === "object" && prev !== null
            ? { ...prev, tutoSwitchCompany: true }
            : prev,
        );
      },
      (err) => {
        console.warn(err);
      },
    );
  };

  const switchCompanyHandler = (idCompany) => {
    const nextCompany = cachedCompanies.find(
      (company) => company.idCompany === idCompany,
    );
    if (!nextCompany || nextCompany.checked) {
      return;
    }

    const nextCompanies = cachedCompanies.map((company) => ({
      ...company,
      checked: company.idCompany === idCompany,
    }));
    setDropdownOpen(false);
    setCompanySelected(nextCompany);
    setCompanyList(nextCompanies);
    setCachedCompanies(nextCompanies);
    setRefreshCompany(!refreshCompany);
  };

  const activeCompany =
    cachedCompanies.find((company) => company.checked) ||
    cachedCompanies.find(
      (company) => company.idCompany === companySelected?.idCompany,
    ) ||
    cachedCompanies[0];

  if (!localStorage.getItem("token")) {
    return null;
  }

  if (isWaiting) {
    return (
      <div className="flex cursor-pointer my-auto animate-fadeIn">
        <div className="border rounded px-3 h-[50px] flex items-center justify-between gap-2 w-[210px] box-border">
          <div className="flex items-center gap-2 min-w-0">
            <div className="shrink-0">
              <div className="rounded bg-gray-100 text-gray-700 w-[36px] h-[36px] flex justify-center items-center">
                <BuildingCompanyIcon className="shrink-0" />
              </div>
            </div>
            <div className="flex flex-col justify-center min-w-0">
              <div className="text-ellipsis whitespace-nowrap max-w-[110px] overflow-hidden">
                <div className="h-4 bg-gray-200 rounded w-20" />
              </div>
            </div>
          </div>
          <div className="shrink-0 flex items-center">
            <ChevronDown wh={20} color="#6B7280" />
          </div>
        </div>
      </div>
    );
  }

  if (cachedCompanies.length < 2 || !activeCompany) {
    return null;
  }

  const triggerCard = (
    <div
      className={`flex ${!createOfMission ? "cursor-pointer" : ""} my-auto`}
      onClick={() => setDropdownOpen(!dropdownOpen)}
    >
      <div
        className={`border rounded px-3 h-[50px] flex items-center justify-between gap-2 w-[210px] box-border ${!createOfMission ? "hover:bg-gray-50 active:bg-gray-100 transition-colors" : ""}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="shrink-0">
            <div className="rounded bg-gray-100 text-gray-700 w-[36px] h-[36px] flex justify-center items-center">
              <BuildingCompanyIcon className="shrink-0" />
            </div>
          </div>
          <div className="flex flex-col justify-center min-w-0">
            <div
              className={`ft-sm text-ellipsis whitespace-nowrap max-w-[110px] overflow-hidden ${!createOfMission ? "" : "text-gray-500"}`}
            >
              {activeCompany.name}
            </div>
          </div>
        </div>
        <div className="shrink-0 flex items-center">
          {!createOfMission && <ChevronDown wh={20} color="#6B7280" />}
        </div>
      </div>
    </div>
  );

  return (
    <div ref={dropDownRef} className="relative select-none">
      {confirmTutoSwitchCompany ? (
        triggerCard
      ) : (
        <ExternalTooltip
          title="Un compte, plusieurs entreprises."
          description="Gérez les données depuis vos autres entreprises depuis cette section."
          confirm={confirmTutoSwitchCompany}
          onConfirm={dismissTutoSwitchCompany}
        >
          {triggerCard}
        </ExternalTooltip>
      )}

      {dropdownOpen && !createOfMission && (
        <div
          className={`${dropdownOpen ? "" : "hidden"} absolute left-0 top-full mt-1 border rounded shadow-lg w-[210px] z-50 bg-white overflow-x-hidden overflow-y-auto max-h-80 box-border`}
        >
          <div className="p-1 flex flex-col">
            {cachedCompanies.map((company, index) => (
              <div
                key={company.idCompany || index}
                className={`flex ${!company.checked ? "cursor-pointer" : ""} my-auto`}
                onClick={() => switchCompanyHandler(company.idCompany)}
              >
                <div
                  className={`min-h-[50px] px-2 flex items-center gap-2 w-full rounded-md box-border ${!company.checked ? "hover:bg-gray-100 active:bg-gray-100 transition-colors" : ""}`}
                >
                  <div className="shrink-0">
                    <div
                      className={`rounded w-[36px] h-[36px] flex justify-center items-center bg-gray-100 text-gray-700`}
                    >
                      {!company.checked ? (
                        <BuildingCompanyIcon className="shrink-0" />
                      ) : (
                        <CheckIcon wh={14} color="#374151" />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col justify-center min-w-0 flex-1">
                    <div
                      className={`ft-sm text-ellipsis whitespace-nowrap overflow-hidden ${company.checked ? "text-gray-700 font-medium" : "text-gray-900"}`}
                    >
                      {company.name}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SwitchCompany;
